'use client';

import { useTranslations } from 'next-intl';
export { fetchEstimatesForStore } from './estimates';
import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { CnoPlusShopifyMap } from 'content/products/addons/cnoPlus';
import { ClueShopifyMap } from 'content/products/addons/clue';
import {
  productMap_air_ring,
  getEngravingKey,
  RingSizes,
  RingChargerType,
  AirRingColors,
  isRingAirSkuDisabled,
} from 'content/products/ring';
import { TravelChargerShopifyMap } from 'content/products/accessories';
import { SizingKitShopifyMap } from 'content/products/ring';
import { UhxShopifyMap } from 'content/products/uhx';
import {
  RingStackType,
  StackVariant,
} from 'content/products/ring/stacking/constants';
import {
  StackingShopifyMap,
  DuoStackingShopifyMap,
} from 'content/products/ring/stacking/StackingShopifyMap';
import {
  AddonsAction,
  AddonsState,
  CheckoutParams,
  RingContextType,
  ShippingEstimateObject,
} from './types';

import { idleSddNddController } from 'components/ring-air/DeliveryEtaBadge/useSddNddEligibility';

import { ReactNode, useRef, useEffect } from 'react';
import styles from './checkout.module.css';

import { DaysByDate } from 'components/shared/DaysbyDate';
import { addToCartArgsType, ProductType } from 'components/shared/Cart/types';
import { tagBundle } from 'components/shared/Cart/bundle';
import { Currency } from 'utils/Currency';

export function getCheckoutLineItems(
  params: CheckoutParams,
): addToCartArgsType[] {
  const {
    color,
    size,
    store,
    chargerType,
    engraving,
    engravingMessage,
    eSizingSize,
    uhxCart,
    afibCart,
    cardioAdaptibilityCart,
    cnoProCart,
    clueCart,
    stackType,
    stackVariant,
    misc,
    isCnOPlus,
    // affiliateDetails,
  } = params;

  if (!color || !size || !store) return [];

  if (
    isRingAirSkuDisabled({
      color,
      size,
      type: getEngravingKey(engraving),
      store,
    })
  ) {
    return [];
  }

  const productNode = productMap_air_ring[color]?.[size];
  if (!productNode) return [];

  let mainVariantId: string | undefined;
  if (misc && productNode.NOT_ENGRAVED.misc?.[misc]) {
    mainVariantId = productNode.NOT_ENGRAVED.misc[misc];
  } else {
    mainVariantId = productNode[getEngravingKey(engraving)]?.variantId[store];
  }
  if (!mainVariantId) return [];

  // Build noteAttributes for engraving/eSizing/trade-in
  const noteAttributes: Array<{ key: string; value: string }> = [];
  if (engraving && engravingMessage)
    noteAttributes.push({ key: 'engraving', value: engravingMessage });
  if (eSizingSize) noteAttributes.push({ key: 'eSizing', value: 'true' });
  if (params.tradeInDevice) {
    noteAttributes.push({
      key: 'trade_in_device',
      value: params.tradeInDevice,
    });
    noteAttributes.push({
      key: 'trade_in_credit',
      value: String(params.tradeInCredit),
    });
    noteAttributes.push({
      key: 'trade_in_serial_number',
      value: params.tradeInSerialNumber,
    });
  }
  if (params.ctaPlacement)
    noteAttributes.push({ key: '_cta_placement', value: params.ctaPlacement });
  if (params.uhxDefault && params.uhxDefault !== 'n/a')
    noteAttributes.push({ key: '_uhx_default', value: params.uhxDefault });
  if (params.sizeDefault && params.sizeDefault !== 'n/a')
    noteAttributes.push({ key: '_size_default', value: params.sizeDefault });

  // Start with the ring line item
  const lineItems: addToCartArgsType[] = [];

  // Add a Sizing Kit if "open" size is chosen
  if (size === RingSizes.OPEN && SizingKitShopifyMap[store]?.shopifyVariantId) {
    lineItems.push({
      shopifyVariantId: SizingKitShopifyMap[store].shopifyVariantId as string,
      productType: ProductType.RING,
    });
  }

  // Add travel charger if selected
  if (
    chargerType === RingChargerType.TRAVEL_CHARGER &&
    TravelChargerShopifyMap[store]?.[size]?.shopifyVariantId
  ) {
    lineItems.push({
      shopifyVariantId: TravelChargerShopifyMap[store][size]
        .shopifyVariantId as string,
      productType: ProductType.RING,
    });
  }

  // Add UHX addon if selected
  if (uhxCart && UhxShopifyMap[store]?.[uhxCart]?.variantId) {
    lineItems.push({
      shopifyVariantId: UhxShopifyMap[store][uhxCart].variantId as string,
      productType: ProductType.GENERIC,
    });
  }

  // DISABLED: Free 3-month UHX campaign ended - keeping for reference
  // // Automatically attach UHX Month3 for affiliate checkouts
  // const affiliateUhxVariantId =
  //   affiliateDetails && store
  //     ? UhxShopifyMap[store as ShopifyStore]?.Month3?.variantId
  //     : null;

  // if (affiliateUhxVariantId) {
  //   lineItems.push({
  //     shopifyVariantId: affiliateUhxVariantId,
  //     productType: ProductType.GENERIC,
  //   });
  // }

  // Add Cardio Adaptibility addon if selected
  if (
    cardioAdaptibilityCart &&
    CardioAdaptibilityShopifyMap[store]?.[cardioAdaptibilityCart]?.variantId
  ) {
    lineItems.push({
      shopifyVariantId: CardioAdaptibilityShopifyMap[store][
        cardioAdaptibilityCart
      ].variantId as string,
      productType: ProductType.RING,
    });
  }

  // Add Afib addon if selected
  if (afibCart && AfibShopifyMap[store]?.[afibCart]?.variantId) {
    lineItems.push({
      shopifyVariantId: AfibShopifyMap[store][afibCart].variantId as string,
      productType: ProductType.RING,
    });
  }

  // Add CNO Pro/Plus addon if selected
  if (cnoProCart) {
    const cnoMap = isCnOPlus ? CnoPlusShopifyMap : CnoProShopifyMap;
    if (cnoMap[store]?.[cnoProCart]?.variantId) {
      lineItems.push({
        shopifyVariantId: cnoMap[store][cnoProCart].variantId as string,
        productType: ProductType.RING,
      });
    }
  }

  if (clueCart === 'Year1' && ClueShopifyMap[store]?.Year1?.variantId) {
    lineItems.push({
      shopifyVariantId: ClueShopifyMap[store].Year1.variantId as string,
      productType: ProductType.RING,
    });
  }

  // DISABLED: Respiratory Health PowerPlug — re-enable by uncommenting
  // // Add Respiratory addon if selected
  // if (
  //   respiratoryCart === 'Year1' &&
  //   RespiratoryShopifyMap[store]?.Year1?.variantId
  // ) {
  //   lineItems.push({
  //     shopifyVariantId: RespiratoryShopifyMap[store].Year1.variantId as string,
  //     productType: ProductType.RING,
  //   });
  // }

  // Add stacking ring if selected
  if (stackType && stackType !== RingStackType.NONE) {
    const stackMap =
      stackVariant === StackVariant.DUO
        ? DuoStackingShopifyMap
        : StackingShopifyMap;
    const stackConfig = stackMap[store]?.[stackType];
    const stackShopifyVariantId = stackConfig?.[size]?.shopifyVariantId;
    if (stackShopifyVariantId) {
      lineItems.push({
        shopifyVariantId: stackShopifyVariantId,
        productType: ProductType.RING,
      });
    }
  }

  //add item last
  lineItems.push({
    shopifyVariantId: mainVariantId,
    productType: ProductType.RING,
    params:
      noteAttributes.length > 0
        ? { productType: 'ring', noteAttributes }
        : undefined,
  });

  return tagBundle(lineItems);
}

export const ShippingLabel = ({
  className,
  mcf = false,
  content,
  justContent = false,
  isMinEstimate = false,
}: {
  className?: string;
  mcf?: boolean;
  content: ReactNode;
  justContent?: boolean;
  isMinEstimate?: boolean;
}) => {
  const t = useTranslations('ring');
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
  }, [content, mcf]);

  const cls = className ? `${styles.root} ${className}` : styles.root;

  if (justContent) {
    return (
      <span key={renderCount.current} className={cls}>
        {content}
      </span>
    );
  }

  return (
    <span className={cls} key={renderCount.current}>
      {/* <span>Dispatched by: {content}</span> */}
      <span>
        {t('ring.ringContext.DISPATCHED_BY')} {content}
      </span>
      {/* {mcf ? (
          <Image
            src={getCompressedAssetUrl('/web_v2/ring-buy/amazon-fba-2.png')}
            alt="Fulfillment by Amazon"
            width={'70'}
            height={'20'}
          />
        ) : null} */}
      {isMinEstimate ? <i className="min-est-dot"></i> : null}
    </span>
  );
};

export function getShippingEstimate({
  color,
  size,
  estimates,
  addonsState,
  t,
  tCommon,
}: {
  color: AirRingColors | null;
  size: RingSizes | null;
  estimates: ShippingEstimateObject | undefined;
  addonsState: AddonsState;
  t: (key: string) => string;
  tCommon: (key: string) => string;
}) {
  if (!estimates || !size || !color) {
    return (
      <ShippingLabel
        content={tCommon('checkout.shippingLabel.content.dispatchTimeBased')}
        justContent={true}
      />
    );
  }

  const selectedSKU =
    productMap_air_ring[color]?.[size]?.NOT_ENGRAVED.SKU ?? null;

  if (!selectedSKU) return null;

  const selectedSKUEstimateObject = estimates[selectedSKU];

  const mcfValidOnSelection = addonsState.engraving.enabled === false;

  if (!selectedSKUEstimateObject) {
    return (
      <ShippingLabel
        content={tCommon(
          'checkout.shippingLabel.content.shippingEstimateAvailable',
        )}
        justContent={true}
      />
    );
  }

  const specificEstimate = (() => {
    if (mcfValidOnSelection && selectedSKUEstimateObject.mcf_for_eta)
      return selectedSKUEstimateObject.date;
    else return selectedSKUEstimateObject.default_custom_eta;
  })();

  const monthFormatter = new Intl.DateTimeFormat('default', {
    month: 'short',
  });

  if (specificEstimate) {
    const minDate = new Date(specificEstimate);
    const minMonth = minDate.getMonth();
    const minDay = minDate.getDate();

    if (size === RingSizes.OPEN) {
      return (
        <ShippingLabel
          mcf={
            mcfValidOnSelection &&
            (selectedSKUEstimateObject?.show_mcf_logo ?? false)
          }
          content={
            <>
              <strong style={{ whiteSpace: 'nowrap' }}>
                <DaysByDate endDate={minDate} />
              </strong>{' '}
              <span>({t('ring.AFTER_SIZE_SELECTION')})</span>
            </>
          }
        />
      );
    }

    const maxDate = new Date(specificEstimate);
    maxDate.setDate(maxDate.getDate() + 2);
    const maxMonth = maxDate.getMonth();
    const maxDay = maxDate.getDate();

    return (
      <ShippingLabel
        mcf={
          mcfValidOnSelection &&
          (selectedSKUEstimateObject?.mcf_for_eta ?? false)
        }
        content={
          <strong>
            {`${monthFormatter.format(minDate)} ${minDay} - ${
              minMonth === maxMonth ? '' : monthFormatter.format(maxDate)
            } ${maxDay}`}
          </strong>
        }
      />
    );
  } else {
    return (
      <ShippingLabel
        content={tCommon(
          'checkout.shippingLabel.content.shippingEstimateAvailable',
        )}
        justContent={true}
      />
    );
  }
}

export const initialRingSelectionContext: RingContextType = {
  size: RingSizes.OPEN,
  setSize: () => undefined,
  setColor: () => undefined,
  chargerType: RingChargerType.OG,
  setChargerType: () => undefined,
  defaultCurrency: Currency.inr,
  engraving: false,
  setEngraving: () => undefined,
  engravingMessage: null,
  setEngravingMessage: () => undefined,
  uhx: false,
  setUhx: () => undefined,
  uhxCart: null,
  setUhxCart: () => undefined,
  afibCart: null,
  setAfibCart: () => undefined,
  clueCart: null,
  setClueCart: () => undefined,
  cardioAdaptibilityCart: null,
  setCardioAdaptabilityCart: () => undefined,
  cnoProCart: null,
  setCnoProCart: () => undefined,
  respiratoryCart: null,
  setRespiratoryCart: () => undefined,
  stackType: null,
  setStackType: () => undefined,
  stackVariant: StackVariant.SINGLE,
  setStackVariant: () => undefined,
  checkout: async () => undefined,
  isLeanBuyFlow: false,
  sizeDefaultKept: false,
  uhxDefaultKept: false,
  getExpressCheckoutLineItems: () => [],
  isLoading: false,
  price: 0,
  ultraHumanPrice: 0,
  effectivePrice: 0,
  chargerPrice: 0,
  shippingEstimate: null,
  selectedEmirate: null,
  setSelectedEmirate: () => undefined,
  sddNdd: idleSddNddController,
  affiliateDetails: null,
  store: null,
  campaignActive: false,
  affiliateDiscount: 0,
  tradeInDevice: null,
  setTradeInDevice: () => undefined,
  tradeInSerialNumber: '',
  setTradeInSerialNumber: () => undefined,
  tradeInCredit: 0,
  tradeInBonus: 0,
  tradeInMaxCredit: 0,
  hasUhx: false,
  goToNextStep: () => undefined,
  effectiveDiscount: { type: '', amount: 0 },
  sectionRefs: {},
  showEngravingSection: false,
  priceDrop: 0,
  notServicable: false,
  notServicableMessage: undefined,
};

export function addonsReducer(
  state: AddonsState,
  action: AddonsAction,
): AddonsState {
  switch (action.type) {
    case 'SET_ENGRAVING':
      return {
        ...state,
        engraving: { ...state.engraving, enabled: action.payload },
      };
    case 'SET_ENGRAVING_MESSAGE':
      return {
        ...state,
        engraving: { ...state.engraving, message: action.payload },
      };
    case 'SET_UHX_CART':
      return { ...state, uhxCart: action.payload };
    case 'SET_AFIB_CART':
      return { ...state, afibCart: action.payload };
    case 'SET_CARDIO_ADAPTIBILITY_CART':
      return { ...state, cardioAdaptibilityCart: action.payload };
    case 'SET_CNO_PRO_CART':
      return { ...state, cnoProCart: action.payload };
    case 'SET_CLUE_CART':
      return { ...state, clueCart: action.payload };
    case 'SET_RESPIRATORY_CART':
      return { ...state, respiratoryCart: action.payload };
    case 'SET_ROSE_GOLD_COLOR':
      return { ...state, roseGoldColor: action.payload };
    case 'SET_TRADE_IN_DEVICE':
      return {
        ...state,
        tradeIn: { ...state.tradeIn, device: action.payload },
      };
    case 'SET_TRADE_IN_SERIAL_NUMBER':
      return {
        ...state,
        tradeIn: { ...state.tradeIn, serialNumber: action.payload },
      };
    case 'RESET_TRADE_IN':
      return { ...state, tradeIn: { device: null, serialNumber: '' } };
    case 'RESET_ALL':
      return { ...initialAddonsState, roseGoldColor: state.roseGoldColor };
    case 'SET_STACK_TYPE':
      return { ...state, stackType: action.payload };
    case 'SET_STACK_VARIANT':
      return { ...state, stackVariant: action.payload };

    default:
      return state;
  }
}

export const initialAddonsState: AddonsState = {
  engraving: { enabled: false, message: null },
  uhxCart: null,
  afibCart: null,
  cardioAdaptibilityCart: null,
  cnoProCart: null,
  clueCart: null,
  respiratoryCart: null,
  roseGoldColor: false,
  tradeIn: { device: null, serialNumber: '' },
  stackType: null,
  stackVariant: StackVariant.SINGLE,
};
